import {captureException} from '@sentry/browser';

import {MAX_REAUTH_ATTEMPTS} from './subscriptionConstants';
import type {SubscriptionState} from './subscriptionTypes';

/**
 * Applies the outcome of a completed `refreshAccessToken` call to the
 * subscription state:
 *   - failed (returned false): refresh already redirected to login — stop.
 *   - succeeded but breaker tripped: token works, AppSync still rejects — stop
 *     and emit a single Sentry warning so the dashboard surfaces this case.
 *   - succeeded: bump `reauthAttempts`, reset the backoff curve, reopen.
 */
export const applyRefreshResult = (refreshed: boolean, state: SubscriptionState) => {
  state.isRefreshing = false;

  if (!refreshed) {
    state.hasStopped = true;
    return;
  }

  state.reauthAttempts += 1;

  if (state.reauthAttempts >= MAX_REAUTH_ATTEMPTS) {
    state.hasStopped = true;
    captureException(
      new Error('AppSync auth refresh succeeded but authorization still fails; stopping reconnect'),
      {level: 'warning', tags: {module: 'appsync'}}
    );
    return;
  }

  state.reconnectAttempts = 0;
  if (!state.isCleaningUp) {
    state.reopenSocket?.();
  }
};
